import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  LinearProgress,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { api } from '../services/api';

interface LoginCredentials {
  Usuario: string;
  Senha: string;
}

interface LoginResponse {
  jwt?: string;
  title?: string;
  detail?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  
  const [credentials, setCredentials] = useState<LoginCredentials>({
    Usuario: '',
    Senha: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    Usuario: '',
    Senha: '',
  });
  const [progressMessage, setProgressMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'success',
  });

  
  // Mutation para login
  const loginMutation = useMutation<LoginResponse, Error, LoginCredentials>({
    mutationFn: (data) => 
      api.post<LoginResponse>('/api/Auth', data).then(res => res.data),
    onSuccess: (data) => {
      if (data.jwt) {
        // Login bem sucedido
        localStorage.setItem('setKeyUser', data.jwt);
        
        // Dispara evento customizado para atualizar estado de autenticação
        window.dispatchEvent(new CustomEvent('loginSuccess'));
        
        setProgress(100);
        setProgressMessage('Login realizado com sucesso!');
        setSnackbar({
          open: true,
          message: 'Login realizado com sucesso!',
          severity: 'success',
        });
        
        // Redireciona para o dashboard após 1 segundo
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      } else {
        // Erro de autenticação
        const errorMessage = data.title && data.detail 
          ? `${data.title} - ${data.detail}`
          : 'Falha ao autenticar usuário';
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        setProgressMessage('Falha ao autenticar usuário.');
        setProgress(0);
      }
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: 'Erro de conexão com o servidor',
        severity: 'error',
      });
      setProgressMessage('Falha ao autenticar usuário.');
      setProgress(0);
    },
  });

  // Simula a barra de progresso
  useEffect(() => {
    if (progress > 0 && progress < 50) {
      const timer = setTimeout(() => {
        setProgress(prev => Math.min(prev + 1, 50));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [progress]);

  const validateForm = (): boolean => {
    const newErrors = {
      Usuario: '',
      Senha: '',
    };
    let isValid = true;

    if (!credentials.Usuario.trim()) {
      newErrors.Usuario = 'Usuário é obrigatório';
      isValid = false;
    }

    if (!credentials.Senha.trim()) {
      newErrors.Senha = 'Senha é obrigatória';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (validateForm()) {
      setProgressMessage('Autenticando usuário...');
      setProgress(1);
      loginMutation.mutate(credentials);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleInputChange = (field: keyof LoginCredentials) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCredentials(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
    
    // Limpa o erro do campo quando o usuário começa a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #d9ada3, #eec3bb, #fad1c9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            backgroundColor: '#fad1c9',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                component="img"
                src="/static/Logo-B.png"
                alt="By Lust Lingerie"
                sx={{
                  height: 300,
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Formulário */}
            <Box
              component="form"
              ref={formRef}
              onSubmit={handleSubmit}
              onKeyPress={handleKeyPress}
              sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <TextField
                fullWidth
                label="Usuário"
                value={credentials.Usuario}
                onChange={handleInputChange('Usuario')}
                error={!!errors.Usuario}
                helperText={errors.Usuario}
                variant="outlined"
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              />

              <TextField
                fullWidth
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={credentials.Senha}
                onChange={handleInputChange('Senha')}
                error={!!errors.Senha}
                helperText={errors.Senha}
                variant="outlined"
                size="medium"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          sx={{ color: '#ee44aa' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              />

              {/* Mensagem de progresso */}
              {progressMessage && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {progressMessage}
                  </Typography>
                </Box>
              )}

              {/* Barra de progresso */}
              {progress > 0 && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(238, 68, 170, 0.2)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#ee44aa',
                      },
                    }}
                  />
                </Box>
              )}

              {/* Botão de login */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loginMutation.isPending}
                color="primary"
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(234, 192, 183, 0.5)',
                    color: 'rgba(33, 33, 33, 0.5)',
                  },
                }}
              >
                {loginMutation.isPending ? 'Autenticando...' : 'Acessar'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
